import type { A } from "ts-toolbelt"

import { api } from "@/api"
import type { MaybeRef } from "@vueuse/core"
import { useQuery } from "@tanstack/vue-query"
import type { TransactionStatus } from "../sharedTypes/transactions"

type RetrieveTransactionResponseShared = {
  id: string
  type: "payment" | "refund"
  createdAt: Date
  status: TransactionStatus
  amountTransactedGross: number
  amountTransactedGratuity: number
  amountTransactedCashback: number
  amountTransactedTotal: number
  currency: string
  orderId: string | null
  donationId: string | null
  isDcc: boolean
  isDccFieldsPopulated: boolean
  dccCardholderCurrency: string
  dccCardCurrencyAmount: number
  terminalSerialNumber: string | null
}

export type RetrieveTransactionResponseCard = A.Compute<
  RetrieveTransactionResponseShared & {
    transactionMethod: "pos_card"
    cardScheme: string
    cardNumberLast4: string
    acquirerResponseCode: string | null
    acquirerTerminalId: string | null
    acquirerAuthCode: string | null
  }
>

export type RetrieveTransactionResponseCash = A.Compute<
  RetrieveTransactionResponseShared & {
    transactionMethod: "pos_cash"
  }
>

export type RetrieveTransactionResponse =
  | RetrieveTransactionResponseCard
  | RetrieveTransactionResponseCash

const retrieveTransaction = async (
  transactionId: string
): Promise<RetrieveTransactionResponse> => {
  const { body } = await api.get<
    RecursivelyConvertDatesToStrings<RetrieveTransactionResponse>
  >(`/transactions/${transactionId}/`)

  return {
    ...body,
    createdAt: DateFns.parseISO(body.createdAt),
  }
}

export const useTransaction = (
  transactionId: MaybeRef<string>
) =>
  useQuery({
    queryKey: [
      BASE_TRANSACTIONS_QUERY_KEY,
      transactionId,
    ] as const,
    queryFn: ({ queryKey: [_, transactionId_] }) =>
      retrieveTransaction(transactionId_),
    refetchInterval: Infinity,
    staleTime: 0,
  })
